// IMPACT Catalog Rating API — REALM VOC integration
// API docs: https://quip-amazon.com/t9WXAAWlK44E

import axios from "axios";
import { EAGLEEYE_PROD_HOSTNAME } from "src/common/constants/utils.const";

const IMPACT_API_BASE_URL = window.location.hostname === EAGLEEYE_PROD_HOSTNAME
    ? "https://api.impact.tools.amazon.dev"
    : "https://api.beta.impact.tools.amazon.dev";

// Separate axios instance — IMPACT authenticates via Midway cookies, not Cognito.
const impactAxios = axios.create({
    baseURL: IMPACT_API_BASE_URL,
    withCredentials: true,
});

/**
 * Pre-authenticate with IMPACT via a hidden iframe.
 *
 * Chrome blocks third-party cookies on cross-origin XHR requests.
 * Loading IMPACT's domain in an iframe triggers Midway's SSO redirect,
 * which sets the amzn_sso_token cookie so subsequent withCredentials
 * requests include it.
 */
let midwayPreAuthDone = false;
let midwayPreAuthPromise: Promise<void> | null = null;

export const ensureImpactMidwayAuth = (): Promise<void> => {
    if (midwayPreAuthDone) return Promise.resolve();
    if (midwayPreAuthPromise) return midwayPreAuthPromise;

    midwayPreAuthPromise = new Promise((resolve) => {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = `${IMPACT_API_BASE_URL}/GetCatalogTools`;

        let settled = false;
        let safetyTimer: ReturnType<typeof setTimeout>;
        let loadTimer: ReturnType<typeof setTimeout>;

        const success = () => {
            if (settled) return;
            settled = true;
            clearTimeout(safetyTimer);
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            midwayPreAuthDone = true;
            resolve();
        };

        const fail = () => {
            if (settled) return;
            settled = true;
            clearTimeout(loadTimer);
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            midwayPreAuthPromise = null;
            resolve();
        };

        iframe.onload = () => { clearTimeout(loadTimer); loadTimer = setTimeout(success, 2000); };
        iframe.onerror = () => fail();
        safetyTimer = setTimeout(fail, 5000);

        document.body.appendChild(iframe);
    });

    return midwayPreAuthPromise;
};

export interface CatalogTool {
    id: string;
    name: string;
}

export interface CatalogRatingPayload {
    toolId: string;
    score: number;
    comment: string;
}

/** Fetch all REALM catalog tools to find EagleEye's toolId. */
export const getCatalogTools = () => {
    return impactAxios.get("/GetCatalogTools");
};

/** Submit (or update) a user rating for EagleEye. IMPACT uses upsert. */
export const putCatalogRating = (payload: CatalogRatingPayload) => {
    return impactAxios.post("/PutCatalogRating", payload);
};
